"use client";

import { useCallback, useState } from "react";
import dynamic from "next/dynamic";
import { useCalendlyEventListener } from "react-calendly";
import Button, { type ButtonVariant } from "./button";
import {
  buildAttributionSnapshot,
  type AttributionSnapshot,
} from "@/app/lib/attributionSnapshot";

const PopupModal = dynamic(
  () => import("react-calendly").then((m) => m.PopupModal),
  { ssr: false }
);

type CalendlyClickMeta = Record<string, unknown>;

interface CalendlyAttribution {
  id: string;
  pagePath: string;
  pageUrl: string;
  calendlyUrl: string;
  snapshot: AttributionSnapshot | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  fbclid: string | null;
  liFatId: string | null;
}

interface CalendlyButtonProps {
  url: string;
  children: React.ReactNode;
  variant?: ButtonVariant;
  className?: string;
  onOpen?: (meta?: CalendlyClickMeta) => void;
}

function createAttributionId(): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return `cal_${random.replace(/[^A-Za-z0-9-]/g, "")}`;
}

function snapshotId(snapshot: AttributionSnapshot | null, key: string): string | null {
  return snapshot?.ids[key]?.value ?? null;
}

async function sendCalendlyAttribution(
  attribution: CalendlyAttribution,
  phase: "open" | "scheduled",
  calendlyEvent?: { eventUri?: string; inviteeUri?: string }
) {
  try {
    await fetch("/api/calendly-attribution", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        attribution_id: attribution.id,
        phase,
        page_path: attribution.pagePath,
        page_url: attribution.pageUrl,
        calendly_url: attribution.calendlyUrl,
        calendly_event_uri: calendlyEvent?.eventUri ?? null,
        calendly_invitee_uri: calendlyEvent?.inviteeUri ?? null,
        gclid: attribution.gclid,
        gbraid: attribution.gbraid,
        wbraid: attribution.wbraid,
        fbclid: attribution.fbclid,
        li_fat_id: attribution.liFatId,
        attribution_snapshot: attribution.snapshot,
      }),
      keepalive: true,
    });
  } catch (error) {
    console.error("[calendly-attribution] request failed:", error);
  }
}

function clickMeta(attribution: CalendlyAttribution): CalendlyClickMeta {
  return {
    calendly_attribution_id: attribution.id,
    has_gclid: Boolean(attribution.gclid),
    has_gbraid: Boolean(attribution.gbraid),
    has_wbraid: Boolean(attribution.wbraid),
    has_fbclid: Boolean(attribution.fbclid),
    has_li_fat_id: Boolean(attribution.liFatId),
  };
}

export default function CalendlyButton({
  url,
  children,
  variant = "red",
  className,
  onOpen,
}: CalendlyButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [rootElement, setRootElement] = useState<HTMLElement | null>(null);
  const [attribution, setAttribution] = useState<CalendlyAttribution | null>(null);

  useCalendlyEventListener({
    onEventScheduled: (event) => {
      if (!attribution) return;

      const eventUri = event.data.payload.event.uri;
      const inviteeUri = event.data.payload.invitee.uri;
      window.dataLayer?.push({
        event: "calendly_event_scheduled",
        calendly_attribution_id: attribution.id,
        calendly_event_uri: eventUri,
        calendly_invitee_uri: inviteeUri,
        page_path: attribution.pagePath,
      });
      void sendCalendlyAttribution(attribution, "scheduled", { eventUri, inviteeUri });
    },
  });

  const handleOpen = useCallback(() => {
    setRootElement(document.getElementById("root") ?? document.body);
    const snapshot = buildAttributionSnapshot();
    const pagePath = `${window.location.pathname}${window.location.search}`;
    const nextAttribution: CalendlyAttribution = {
      id: createAttributionId(),
      pagePath,
      pageUrl: window.location.href,
      calendlyUrl: url,
      snapshot,
      gclid: snapshotId(snapshot, "gclid"),
      gbraid: snapshotId(snapshot, "gbraid"),
      wbraid: snapshotId(snapshot, "wbraid"),
      fbclid: snapshotId(snapshot, "fbclid"),
      liFatId: snapshotId(snapshot, "li_fat_id"),
    };

    setAttribution(nextAttribution);
    onOpen?.(clickMeta(nextAttribution));
    void sendCalendlyAttribution(nextAttribution, "open");
    setIsOpen(true);
  }, [onOpen, url]);

  return (
    <>
      <Button
        variant={variant}
        className={className}
        onClick={handleOpen}
      >
        {children}
      </Button>
      {rootElement && (
        <PopupModal
          url={url}
          open={isOpen}
          onModalClose={() => setIsOpen(false)}
          rootElement={rootElement}
          utm={
            attribution
              ? {
                  utmSource: "tmrw-marketing",
                  utmMedium: "website",
                  utmCampaign: "calendly-lead",
                  utmContent: attribution.id,
                  utmTerm: attribution.pagePath,
                }
              : undefined
          }
        />
      )}
    </>
  );
}
